import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/apiConfig';
import { FinancialAccount } from '../../types/finance';
import { Alert, Spinner, Badge } from '../ui';
import { Panel } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';
import { TruckIcon, HomeIcon } from '@heroicons/react/24/outline';

const ASSET_TYPES_WITH_EQUITY = ['vehicle', 'real_estate'];

const AssetCard: React.FC<{ asset: FinancialAccount; formatCurrency: (n: number | string) => string }> = ({ asset, formatCurrency }) => {
  const vm = asset.vehicle_metadata;
  const loan = asset.linked_loan;
  const isVehicle = asset.account_type === 'vehicle';
  const Icon = isVehicle ? TruckIcon : HomeIcon;
  const title = isVehicle && vm
    ? `${vm.year || ''} ${vm.make} ${vm.model}`.trim()
    : asset.name;
  const valueLabel = isVehicle ? 'Market Value' : 'Property Value';

  return (
    <Panel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
            <Icon style={{ width: '24px', height: '24px', color: COLORS.accent.default }} />
            <div>
              <h3 style={{
                margin: 0,
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                fontWeight: TYPOGRAPHY.weight.medium,
                color: COLORS.text.primary,
              }}>{title}</h3>
              {isVehicle && vm?.vin && (
                <span style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary }}>
                  VIN: {vm.vin}
                </span>
              )}
              {!isVehicle && asset.institution_name && (
                <span style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary }}>
                  {asset.institution_name}
                </span>
              )}
            </div>
          </div>
          <Badge variant="success" size="sm">
            {asset.account_type_display}
          </Badge>
        </div>

        {/* Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: loan ? '1fr 1fr 1fr' : '1fr', gap: SPACING[16] }}>
          <div style={{
            padding: SPACING[16],
            backgroundColor: COLORS.bg.base,
            borderRadius: RADIUS,
            border: `1px solid ${COLORS.border.default}`,
          }}>
            <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary, marginBottom: SPACING[4], textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {valueLabel}
            </div>
            <div style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontWeight: TYPOGRAPHY.weight.bold, color: COLORS.status.success }}>
              {formatCurrency(asset.current_balance)}
            </div>
            {asset.last_synced_at && (
              <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary, marginTop: SPACING[4] }}>
                Updated {new Date(asset.last_synced_at).toLocaleDateString()}
              </div>
            )}
          </div>

          {loan && (
            <div style={{
              padding: SPACING[16],
              backgroundColor: COLORS.bg.base,
              borderRadius: RADIUS,
              border: `1px solid ${COLORS.border.default}`,
            }}>
              <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary, marginBottom: SPACING[4], textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {isVehicle ? 'Loan Balance' : 'Mortgage Balance'}
              </div>
              <div style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontWeight: TYPOGRAPHY.weight.bold, color: COLORS.status.error }}>
                {formatCurrency(loan.balance)}
              </div>
              <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary, marginTop: SPACING[4] }}>
                {loan.name}
              </div>
            </div>
          )}

          {loan && (
            <div style={{
              padding: SPACING[16],
              backgroundColor: COLORS.bg.base,
              borderRadius: RADIUS,
              border: `1px solid ${COLORS.border.default}`,
            }}>
              <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary, marginBottom: SPACING[4], textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Equity
              </div>
              <div style={{
                fontSize: TYPOGRAPHY.headings.sizes.h2,
                fontWeight: TYPOGRAPHY.weight.bold,
                color: loan.equity >= 0 ? COLORS.status.success : COLORS.status.error,
              }}>
                {formatCurrency(loan.equity)}
              </div>
              <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary, marginTop: SPACING[4] }}>
                {loan.equity >= 0 ? 'Positive equity' : 'Underwater'}
              </div>
            </div>
          )}
        </div>

        {/* Vehicle Details */}
        {isVehicle && vm && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: SPACING[12],
          }}>
            {vm.make && (
              <div>
                <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Make</div>
                <div style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>{vm.make}</div>
              </div>
            )}
            {vm.model && (
              <div>
                <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Model</div>
                <div style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>{vm.model}</div>
              </div>
            )}
            {vm.year && (
              <div>
                <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Year</div>
                <div style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>{vm.year}</div>
              </div>
            )}
            {vm.mileage != null && (
              <div>
                <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mileage</div>
                <div style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>{vm.mileage.toLocaleString()} mi</div>
              </div>
            )}
            {vm.condition && (
              <div>
                <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Condition</div>
                <div style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, textTransform: 'capitalize' }}>{vm.condition}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
};

const VehiclesView: React.FC = () => {
  const [assets, setAssets] = useState<FinancialAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const response = await apiClient.get('/api/financial-accounts/');
        const all: FinancialAccount[] = response.data || [];
        setAssets(all.filter(a => ASSET_TYPES_WITH_EQUITY.includes(a.account_type) && a.is_active));
      } catch (err) {
        setError('Failed to load assets.');
      } finally {
        setLoading(false);
      }
    };
    fetchAssets();
  }, []);

  const formatCurrency = (amount: number | string) => {
    const n = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(isNaN(n) ? 0 : n);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: SPACING[24] }}>
        <Spinner size="md" />
      </div>
    );
  }

  if (error) return <Alert variant="error">{error}</Alert>;

  const properties = assets.filter(a => a.account_type === 'real_estate');
  const vehicles = assets.filter(a => a.account_type === 'vehicle');

  if (assets.length === 0) {
    return (
      <Panel>
        <div style={{ textAlign: 'center', padding: SPACING[48] }}>
          <HomeIcon style={{ margin: '0 auto', height: '48px', width: '48px', color: COLORS.text.tertiary }} />
          <p style={{ marginTop: SPACING[8], fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary }}>
            No properties or vehicles found. Add them in the Accounts tab.
          </p>
        </div>
      </Panel>
    );
  }

  // Total equity across all assets with loans
  const totalEquity = assets.reduce((sum, a) => sum + (a.linked_loan?.equity ?? 0), 0);
  const totalValue = assets.reduce((sum, a) => sum + (Number(a.current_balance) || 0), 0);
  const totalDebt = assets.reduce((sum, a) => sum + (a.linked_loan?.balance ?? 0), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {/* Summary */}
      {totalDebt > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: SPACING[16] }}>
          <Panel>
            <div style={{ textAlign: 'center', padding: SPACING[8] }}>
              <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: SPACING[4] }}>
                Total Asset Value
              </div>
              <div style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontWeight: TYPOGRAPHY.weight.bold, color: COLORS.status.success }}>
                {formatCurrency(totalValue)}
              </div>
            </div>
          </Panel>
          <Panel>
            <div style={{ textAlign: 'center', padding: SPACING[8] }}>
              <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: SPACING[4] }}>
                Total Debt
              </div>
              <div style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontWeight: TYPOGRAPHY.weight.bold, color: COLORS.status.error }}>
                {formatCurrency(totalDebt)}
              </div>
            </div>
          </Panel>
          <Panel>
            <div style={{ textAlign: 'center', padding: SPACING[8] }}>
              <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: SPACING[4] }}>
                Total Equity
              </div>
              <div style={{
                fontSize: TYPOGRAPHY.headings.sizes.h2,
                fontWeight: TYPOGRAPHY.weight.bold,
                color: totalEquity >= 0 ? COLORS.status.success : COLORS.status.error,
              }}>
                {formatCurrency(totalEquity)}
              </div>
            </div>
          </Panel>
        </div>
      )}

      {/* Properties */}
      {properties.length > 0 && (
        <>
          <h3 style={{
            margin: 0,
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h3,
            fontWeight: TYPOGRAPHY.weight.medium,
            color: COLORS.text.secondary,
          }}>Properties</h3>
          {properties.map(p => <AssetCard key={p.id} asset={p} formatCurrency={formatCurrency} />)}
        </>
      )}

      {/* Vehicles */}
      {vehicles.length > 0 && (
        <>
          <h3 style={{
            margin: 0,
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h3,
            fontWeight: TYPOGRAPHY.weight.medium,
            color: COLORS.text.secondary,
          }}>Vehicles</h3>
          {vehicles.map(v => <AssetCard key={v.id} asset={v} formatCurrency={formatCurrency} />)}
        </>
      )}
    </div>
  );
};

export default VehiclesView;
